import { LazyImage, type LazyImageProps, type RefArg } from '@fergdigitalcommerce/react-lazy-images';
import { type FunctionComponent } from 'react';
import { BASE_FONT_SIZE_PX } from '../../../constants/general';
import { fallbackImage } from '../../../helpers/images/images.helper';
import { isChromatic } from '../../../utils/dom/chromatic.utils';
import { doesWindowExist } from '../../../utils/dom/dom.utils';
import type { BaseImageProps } from '../image.types';

/**
 * Converts pixel value to rem assuming a 16px root font.
 * Used in cases when you want to use native api to get pixel values to do
 * calculations with and then set style attribute in rem.
 */
const pxToRem = (px: number): string => `${px / BASE_FONT_SIZE_PX}rem`;

export const BaseImage: FunctionComponent<BaseImageProps> = ({
	actualImageProps = {},
	bypassLazyLoad,
	className,
	customStyles = {},
	description,
	dimensions = {},
	draggable,
	ignoreChromatic = false,
	lowQualityPlaceholder,
	limitHeight = false,
	shouldUseDimensions,
	source,
	sourceSetData
}) => {
	const isJestRunning = doesWindowExist() && window.isJestRunning;
	// always bypass lazy loading if we are running tests or chromatic
	bypassLazyLoad = bypassLazyLoad || isChromatic() || isJestRunning;
	const placeholderSrc = lowQualityPlaceholder || fallbackImage;
	const { width, height } = dimensions;
	const heightInRem = pxToRem(height || 0);
	const placeholderStyles = lowQualityPlaceholder ? { width, filter: 'blur(5px)' } : undefined;
	const finalDimensions = shouldUseDimensions ? dimensions : {};

	const placeholder = ({ ref }: Partial<RefArg>) => (
		<img
			alt={description}
			className="tc center"
			draggable={draggable}
			ref={ref}
			src={placeholderSrc}
			style={width ? placeholderStyles : { height: heightInRem }}
			{...finalDimensions}
		/>
	);

	const actual = ({ imageProps }) => (
		<img
			alt={description}
			className={className}
			data-chromatic={ignoreChromatic ? 'ignore' : undefined}
			draggable={draggable}
			{...actualImageProps}
			{...finalDimensions}
			style={{ ...customStyles, maxHeight: limitHeight ? heightInRem : undefined }}
			{...imageProps}
		/>
	);

	const lazyImageProps: LazyImageProps = {
		src: source,
		loadEagerly: bypassLazyLoad,
		placeholder,
		actual
	};

	// conditionally set responsive attributes if they are available
	if (sourceSetData && sourceSetData.srcSet) {
		const { srcSet, sizes } = sourceSetData;
		lazyImageProps.srcSet = srcSet;
		lazyImageProps.sizes = sizes;
	}

	return <LazyImage {...lazyImageProps} />;
};
